import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

export interface ToastAction {
  readonly label: string;
  readonly run: () => void;
}

interface Toast {
  readonly id: number;
  readonly text: string;
  readonly action?: ToastAction;
  readonly tone: 'info' | 'error';
}

type Show = (
  text: string,
  options?: { action?: ToastAction; tone?: 'info' | 'error'; durationMs?: number },
) => void;

const ToastContext = createContext<Show>(() => undefined);

/** Says what just happened, with a way back ("Undo") when there is one. */
export function useToast(): Show {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const dismiss = useCallback(
    (id: number) => setToasts((list) => list.filter((t) => t.id !== id)),
    [],
  );
  const show = useCallback<Show>(
    (text, options = {}) => {
      const id = next.current++;
      setToasts((list) => [
        ...list.slice(-2),
        {
          id,
          text,
          tone: options.tone ?? 'info',
          ...(options.action ? { action: options.action } : {}),
        },
      ]);
      // Long enough to read what went wrong, or to reach the button.
      const ms = options.tone === 'error' ? 10000 : options.action ? 8000 : 4000;
      window.setTimeout(() => dismiss(id), options.durationMs ?? ms);
    },
    [dismiss],
  );
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast ${t.tone}`}
            {...(t.tone === 'error' ? { role: 'alert' } : {})}
          >
            <span>{t.text}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action?.run();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            {t.tone === 'error' && (
              <button
                type="button"
                className="toast-close"
                aria-label="Dismiss"
                onClick={() => dismiss(t.id)}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
